import { useCallback, useEffect, useRef, useState } from 'react';
import { getBaseRequest } from '@components/config/axiosClient';

const DEBOUNCE_MS = 220;

/**
 * How many products the dropdown asks for at a time: the best ones first, then the next ones as it is scrolled. A small batch keeps
 * each answer and each draw of the list small, however many products match.
 */
export const DROPDOWN_PAGE_SIZE = 10;

/**
 * How many products the dropdown will list at the most: as far as the gateway can reach (SearchResultsRequest::MAX_REACH, which is
 * Sierra's own limit). Past it the list stops asking and "Browse all" is how the rest are reached.
 */
export const DROPDOWN_MAX_PRODUCTS = 500;

const INITIAL_STATE = Object.freeze({
    products: [],
    total: 0,
    loading: false,
    loadingMore: false,
    moreFailed: false,
    error: null,
});

const idOf = (product) => product.product_id ?? product.id ?? product.product_code;

const searchAddress = (term, offset) => (
    `get-search-results?term=${encodeURIComponent(term)}&limit=${DROPDOWN_PAGE_SIZE}${offset > 0 ? `&offset=${offset}` : ''}`
);

/**
 * Custom hook for debounced live product search as the user types, loading more of the matches as the list is scrolled.
 * Queries Sierra's unified product index via /api/get-search-results.
 * Drops stale out-of-order responses: a batch that was asked for before the text changed is never added to the new list.
 *
 * `loadMore()` asks for the next batch (and does nothing while one is coming, or when every match is in the list); after a failed
 * batch it waits for `loadMore({ retry: true })`, so a failing server is not asked again and again by the scrolling.
 *
 * @param {{ query: string, open: boolean, debounceMs?: number }} options
 */
export default function useLiveProductSearch({ query, open, debounceMs = DEBOUNCE_MS }) {
    const [state, setState] = useState(INITIAL_STATE);
    const generation = useRef(0);
    const searchedTerm = useRef('');
    const latestState = useRef(state);
    latestState.current = state;
    const batchComing = useRef(false);

    const executeSearch = useCallback(async (searchTerm) => {
        const trimmed = searchTerm.trim();
        if (!trimmed) {
            generation.current += 1;
            searchedTerm.current = '';
            batchComing.current = false;
            setState(INITIAL_STATE);
            return;
        }

        generation.current += 1;
        const mine = generation.current;
        searchedTerm.current = trimmed;
        batchComing.current = false;
        setState((prev) => ({ ...prev, loading: true, loadingMore: false, moreFailed: false, error: null }));

        try {
            const response = await getBaseRequest(searchAddress(trimmed, 0));
            if (mine !== generation.current) {
                return;
            }

            const data = response?.data;
            if (data && data.status === 'success') {
                const productList = Array.isArray(data.products) ? data.products : [];
                setState({ ...INITIAL_STATE, products: productList, total: data.total ?? productList.length });
            } else {
                setState(INITIAL_STATE);
            }
        } catch (err) {
            if (mine !== generation.current) {
                return;
            }
            setState({ ...INITIAL_STATE, error: 'Could not load search results.' });
        }
    }, []);

    const loadMore = useCallback(async ({ retry = false } = {}) => {
        const current = latestState.current;
        const term = searchedTerm.current;
        if (term === '' || batchComing.current || current.loading) return;
        if (current.products.length >= Math.min(current.total, DROPDOWN_MAX_PRODUCTS)) return;
        if (current.moreFailed && !retry) return;

        batchComing.current = true;
        const mine = generation.current;
        setState((prev) => ({ ...prev, loadingMore: true, moreFailed: false }));

        try {
            const response = await getBaseRequest(searchAddress(term, current.products.length));
            if (mine !== generation.current) return;

            const data = response?.data;
            if (!data || data.status !== 'success') {
                setState((prev) => ({ ...prev, loadingMore: false, moreFailed: true }));
                return;
            }

            const batch = Array.isArray(data.products) ? data.products : [];
            setState((prev) => {
                const held = new Set(prev.products.map(idOf));
                const added = batch.filter((product) => !held.has(idOf(product)));
                // A batch with nothing new would be asked for again and again: the matches the index holds are all here.
                const total = added.length === 0 ? prev.products.length : (data.total ?? prev.total);
                return { ...prev, products: [...prev.products, ...added], total, loadingMore: false };
            });
        } catch (err) {
            if (mine !== generation.current) return;
            setState((prev) => ({ ...prev, loadingMore: false, moreFailed: true }));
        } finally {
            if (mine === generation.current) batchComing.current = false;
        }
    }, []);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const trimmed = query.trim();
        if (!trimmed) {
            generation.current += 1;
            searchedTerm.current = '';
            batchComing.current = false;
            setState(INITIAL_STATE);
            return undefined;
        }

        const timer = setTimeout(() => {
            executeSearch(trimmed);
        }, debounceMs);

        return () => clearTimeout(timer);
    }, [query, open, debounceMs, executeSearch]);

    return { ...state, hasMore: state.products.length < Math.min(state.total, DROPDOWN_MAX_PRODUCTS), loadMore };
}
